// components/Page2.js
import { useState, useEffect, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCog } from "@fortawesome/free-solid-svg-icons"
import { useDispatch, useSelector } from "react-redux"
import { useIntl, FormattedMessage } from "react-intl"
import { useForm } from "react-hook-form"
import { toast } from "react-toastify"

import {
  setAreRequirementsSend,
  setCandidates,
  storeJob,
  setJobStatus,
  setRequirements,
  setSelectedCVs,
  setTitle,
  storeJobInformation,
  storeJobSkill,
} from "../../../redux/slices/jobSlice"
import { updateMatch } from "../../../redux/slices/matchSlice"

import { CustomLink, MaxCandidateSelect, SignupInput, Spinner } from "../../common"
import { Requirements, Weights } from "./components"
import { GA_EVENTS } from "../../../constants/analytics"

import { useFetchJobById } from "../../../hooks/useFetchJobById"
import { useTokens } from "../../../hooks/useTokens"
import { usePendingToasts } from "../../../hooks/usePendingToasts"

import { postMatchRequirements, updateJobInformation, updateJobSkills } from "../../../services/jobs"
import { useWorkspaceWebSocket } from "../../../services/useWorkspaceWebSocket"

const DEFAULT_WEIGHTS = {
  hard_skills_weight: 3,
  soft_skills_weight: 3,
  education_weight: 3,
  experience_weight: 3,
};

const useProcessingJobWebSocketConnection = (jobId = null) => {
  const dispatch = useDispatch()
  const [processingJobId, setProcessingJobId] = useState(jobId);

  const jobChunkProcessedCallback = (data) => {
    if (!data?.id) return;

    if (!processingJobId) {
      setProcessingJobId(data.id);
    }

    if (data.information) {
      dispatch(storeJobInformation(data));
    }

    if (data.skill) {
      dispatch(storeJobSkill(data));
    }
  };

  useWorkspaceWebSocket({ connect: true, jobChunkProcessedCallback });

  return {
    processingJobId,
  }
}

const ProcessingMessage = () => (
  <div className="step">
    <FontAwesomeIcon icon={faCog} spin />{" "}
    <FormattedMessage id="common.processing" />
    (<FormattedMessage id="common.processingTime" />)
  </div>
);

const JobInformationSection = ({ editingLoading, jobInfo, onUpdate }) => {
  const { formatMessage } = useIntl()
  const storedTitle = useSelector((state) => state.jobs.title)

  const editableInitialState = {
    activity_sector: false,
    company: false,
    contract_type: false,
    description: false,
    job_type: false,
    title: false,
  }

  const [editable, setEditable] = useState({ ...editableInitialState });

  const {
    defaultValues,
    register,
    handleSubmit,
    formState: {
      isDirty: valuesHaveChanged, isValid: isValidForm, errors: formErrors, touchedFields, isSubmitSuccessful, isSubmitting
    },
    reset,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      activity_sector: jobInfo?.activity_sector || "N/A",
      company: jobInfo?.company || "N/A",
      contract_type: jobInfo?.contract_type || "N/A",
      description: jobInfo?.description || "N/A",
      job_type: jobInfo?.job_type || "N/A",
      title: jobInfo?.title || storedTitle || "N/A",
    },
  })

  const [
    activity_sector,
    company,
    contract_type,
    description,
    job_type,
    title
  ] = watch([
    "activity_sector",
    "company",
    "contract_type",
    "description",
    "job_type",
    "title",
  ]);

  useEffect(() => {
    // Reset the form with new values when jobInfo updates
    reset({
      activity_sector: jobInfo?.activity_sector || "N/A",
      company: jobInfo?.company || "N/A",
      contract_type: jobInfo?.contract_type || "N/A",
      description: jobInfo?.description || "N/A",
      job_type: jobInfo?.job_type || "N/A",
      title: jobInfo?.title || storedTitle || "N/A",
    })
  }, [
    reset,
    storedTitle,
    jobInfo,
    isSubmitSuccessful
  ]);

  const inputSharedProps = {
    smallText: true,
    noBackground: true,
    whiteText: true,
    noPadding: true,
  };

  const onSaveChanges = async (values) => {
    const updatedJobInfo = {
      ...jobInfo,
      activity_sector: values.activity_sector,
      company: values.company,
      contract_type: values.contract_type,
      description: values.description,
      job_type: values.job_type,
      title: values.title,
    };

    await onUpdate(updatedJobInfo);
  };

  if (!jobInfo) return null;

  const disabled = editingLoading || isSubmitting;
  const editLabel = disabled ? "" : formatMessage({ id: "common.edit" });

  return (
    <div className="requirements-page__topbar">
      <SignupInput
        bigText
        noBackground
        noPadding
        whiteText
        name="title"
        placeholder={formatMessage({ id: "common.title" })}
        label={editLabel}
        register={register("title", {
          required: formatMessage({ id: "common.titleRequired" }),
        })}
        error={formErrors.title?.message}
        touched={touchedFields.title}
        value={title}
        editable={editable.title}
        editButtonText={editLabel}
        disabled={disabled}
        setEditable={() => setEditable((prev) => ({ ...prev, title: true }))}
      />

      {jobInfo && <>
        <p className="requirements-page__topbar topbar-info">
          <span>{`${formatMessage({ id: "common.activitySector" })}:`}</span>
          <SignupInput
            {...inputSharedProps}
            name="activity_sector"
            placeholder={formatMessage({ id: "common.activitySector" })}
            label={editLabel}
            register={register("activity_sector")}
            error={formErrors.activity_sector?.message}
            touched={touchedFields.activity_sector}
            value={activity_sector}
            editable={editable.activity_sector}
            editButtonText={editLabel}
            disabled={disabled}
            setEditable={() => setEditable((prev) => ({ ...prev, activity_sector: true }))}
          />
        </p>

        <p className="requirements-page__topbar topbar-info">
          <span>{`${formatMessage({ id: "common.company" })}:`}</span>
          <SignupInput
            {...inputSharedProps}
            name="company"
            placeholder={formatMessage({ id: "common.company" })}
            label={editLabel}
            register={register("company")}
            error={formErrors.company?.message}
            touched={touchedFields.company}
            value={company}
            editable={editable.company}
            editButtonText={editLabel}
            disabled={disabled}
            setEditable={() => setEditable((prev) => ({ ...prev, company: true }))}
          />
        </p>

        <p className="requirements-page__topbar topbar-info">
          <span>{`${formatMessage({ id: "common.contractType" })}:`}</span>
          <SignupInput
            {...inputSharedProps}
            name="contract_type"
            placeholder={formatMessage({ id: "common.contractType" })}
            label={editLabel}
            register={register("contract_type")}
            error={formErrors.contract_type?.message}
            touched={touchedFields.contract_type}
            value={contract_type}
            editable={editable.contract_type}
            editButtonText={editLabel}
            disabled={disabled}
            setEditable={() => setEditable((prev) => ({ ...prev, contract_type: true }))}
          />
        </p>

        <p className="requirements-page__topbar topbar-info">
          <span>{`${formatMessage({ id: "common.jobType" })}:`}</span>
          <SignupInput
            {...inputSharedProps}
            name="job_type"
            placeholder={formatMessage({ id: "common.jobType" })}
            label={editLabel}
            register={register("job_type")}
            error={formErrors.job_type?.message}
            touched={touchedFields.job_type}
            value={job_type}
            editable={editable.job_type}
            editButtonText={editLabel}
            disabled={disabled}
            setEditable={() => setEditable((prev) => ({ ...prev, job_type: true }))}
          />
        </p>

        <p className="requirements-page__topbar topbar-info">
          <span>{`${formatMessage({ id: "common.description" })}:`}</span>
          <SignupInput
            {...inputSharedProps}
            name="description"
            type="textarea"
            placeholder={formatMessage({ id: "common.description" })}
            label={editLabel}
            register={register("description")}
            error={formErrors.description?.message}
            touched={touchedFields.description}
            value={description}
            editable={editable.description}
            editButtonText={editLabel}
            disabled={disabled}
            setEditable={() => setEditable((prev) => ({ ...prev, description: true }))}
          />
        </p>

        {valuesHaveChanged && isValidForm && !isSubmitting &&
          <div className="requirements-page__topbar form-actions">
            <button
              onClick={() => reset(defaultValues)}
              className="btn senary btn--small"
            >
              <FormattedMessage id="common.reset" />
            </button>
            <button
              onClick={handleSubmit(onSaveChanges)}
              className="btn primary btn--small"
            >
              <FormattedMessage id="common.saveChanges" />
            </button>
          </div>
        }

        {(isSubmitting || editingLoading) && 
          <div className="requirements-page__topbar form-actions">
            <Spinner variant="secondary"/>
          </div>
        }
      </>}
    </div>
  );
}

const RequirementsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const params = useParams();
  const { jobId } = params;

  const { availableTokens, refetch: getTokens, isFetching: isFetchingTokens } = useTokens({});
  
  // Handle pending toasts from previous pages
  usePendingToasts();

  const [submitLoading, setSubmitLoading] = useState(false);
  const [editingLoading, setEditingLoading] = useState(false);
  const [weightsData, setWeightsData] = useState({});
  const [haveRequirementsChanged, setHaveRequirementsChanged] = useState(false);
  const [data, setData] = useState(null);

  const matchingTokenCost = useSelector((state) => state.config.tokenCosts.JOB_MATCHING)
  const jobStatus = useSelector((state) => state.jobs.jobStatus)
  const storedJob = useSelector((state) => state.jobs.jobs?.[jobId])
  const { alreadyMatchedOnce, selectedCVs } = useSelector((state) => state.jobs)

  useProcessingJobWebSocketConnection(jobId);

  const updateSelectedJobRequirementsInfo = useCallback((jobData) => {
    if (jobData.selected_candidates?.length) {
      dispatch(setSelectedCVs(jobData.selected_candidates))
    }

    if (jobData.job_requirements) {
      dispatch(
        setRequirements({
          jobId: jobData.id,
          requirements: jobData.job_requirements,
        })
      )
    }

    setData({ ...jobData })
    dispatch(setTitle(jobData.information?.title ?? ""))

    // TODO: Set matching weights from the matching data instead of the default ones
    setWeightsData(DEFAULT_WEIGHTS)
  }, [dispatch])

  const { isFetching: loading } = useFetchJobById({
    id: jobId,
    enabled: jobId && !storedJob,
    callback: (jobData) => {
      if (jobData.id) {
        dispatch(storeJob(jobData))
        dispatch(setJobStatus("COMPLETED"))
        updateSelectedJobRequirementsInfo(jobData)
      }
    },
    onMaxRetryCallback: () => navigate("/job-match/select-cv")
  })

  useEffect(() => {
    if (!jobId) {
      navigate("/job-match/select-cv")
    }

    if (storedJob) {
      updateSelectedJobRequirementsInfo(storedJob)
    }
  }, [jobId, storedJob, updateSelectedJobRequirementsInfo, navigate])

  const handleSubmit = async ({ maxCandidates, threshold }) => {
    if (!maxCandidates && !threshold) return;

    // First update the job requirements if they have changed
    // This is to ensure that the job requirements are saved before matching
    if (haveRequirementsChanged) {
      await onRequirementsEdition();
    }

    setSubmitLoading(true)

    let data_api = {
      ad_id: jobId,
      weights: weightsData,
    }

    const filteredCVs = selectedCVs?.filter(Boolean)
    if (!filteredCVs?.length) {
      toast.error(formatMessage({ id: "toast.errorSelectingCandidates" }))
      setSubmitLoading(false)
      return
    }

    if (threshold) {
      data_api.job_meta = {
        threshold,
      }

      data_api.max_candidates = filteredCVs?.length || 1
    } else {
      data_api.max_candidates = maxCandidates + 15
      data_api.job_meta = {
        actual_topK: maxCandidates,
      }
    }

    data_api.candidate_ids = filteredCVs
    // send a post request to the API; data_api is the data to be sent
    try {
      const matchingResult = await postMatchRequirements(data_api)

      if (!matchingResult) throw new Error("No matching result")

      dispatch(updateMatch(matchingResult));
      dispatch(setCandidates({ candidates: matchingResult.candidates }))
    } catch (error) {
      toast.error(formatMessage({ id: "toast.errorOccurred" }));
      setSubmitLoading(false);
      await getTokens();
      return
    }

    dispatch(setAreRequirementsSend(true))

    await getTokens();

    setSubmitLoading(false)
    navigate(`/job-match/${jobId}/matches`)
  }

  // Resolve the authoritative job id for PATCHes — a cached response carries
  // the clone's id in `id` and the origin's id in `originalId`; KGE stores the
  // origin, so we always PATCH against originalId when present.
  const resolveJobId = () => data.originalId || data.id;

  const handleEdit = async (apiCall, onEditSuccess) => {
    try {
      const updatedJob = await apiCall();

      dispatch(storeJob(updatedJob));
      onEditSuccess?.();
      setEditingLoading(false);
    } catch (error) {
      console.error("Error editing job:", error);
      toast.error(formatMessage({ id: "toast.somethingWentWrongTryAgain" }));
      setEditingLoading(false);
      setHaveRequirementsChanged(true);
      return;
    }
  }

  const onInfoEdition = async (updatedInfo) => {
    setEditingLoading(true);
    const jobId = resolveJobId();
    const mergedInformation = {
      ...data.information,
      ...updatedInfo,
    };
    const onEditSuccess = () => toast.success(formatMessage({ id: "toast.jobInfoUpdatedSuccessfully" }));
    await handleEdit(
      () => updateJobInformation(jobId, mergedInformation),
      onEditSuccess,
    );
  }

  const onRequirementsEdition = async () => {
    setHaveRequirementsChanged(false);
    setEditingLoading(true);
    const jobId = resolveJobId();
    const onEditSuccess = () => toast.success(formatMessage({ id: "toast.jobRequirementsUpdatedSuccessfully" }));
    await handleEdit(
      () => updateJobSkills(jobId, {
        hard_skills: data.hard_skills,
        soft_skills: data.soft_skills,
      }),
      onEditSuccess,
    );
  }

  // TODO: Check when to show the loading state
  // if (loading) {
  //   return (
  //     <div className="requirements-page">
  //       <div className="step">
  //         <FontAwesomeIcon icon={faCog} spin />{" "}
  //         <FormattedMessage id="common.loading" />
  //       </div>
  //     </div>
  //   )
  // }

  if ((loading && !storedJob) || submitLoading) {
    return (
      <div className="requirements-page">
        <ProcessingMessage />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="requirements-page">
        <div className="step">
          <FormattedMessage id="toast.somethingWentWrongTryAgain" />
        </div>
      </div>
    );
  }

  const pageTitle = jobStatus !== "PROCESSING" && (
    <div className="requirements-page__header">
      <h3 className="requirements-page__title h3">
        <FormattedMessage id="message.checkThroughMessage" />{" "}
        <span>
          <FormattedMessage id="message.skillsForRole" />
        </span>
      </h3>
      <p className="p">
        <FormattedMessage id="message.calculateScoresMessage" />
      </p>
    </div>
  );

  const updateRequirementsMessage = alreadyMatchedOnce && (
    <p className="requirements-page__info">
      <FormattedMessage id="message.updateRequirementsMessage" />
      <CustomLink
        to={`/job-match/${jobId}/matches`}
        trackingEvent={GA_EVENTS.REQUIREMENTS.CANCEL_AND_BACK}
      >
        <FormattedMessage id="common.cancelAndGoBack" />
      </CustomLink>
    </p>
  );

  const addMoreCreditsMessage = availableTokens < matchingTokenCost && (
    <p className="requirements-page__info">
      <FormattedMessage id={`message.${availableTokens <= 0 ? "youHaveUsedYourTokens" : "notEnoughTokens"}`} />

      <CustomLink
        to={"/buy-more"}
        target="_blank"
        trackingEvent={GA_EVENTS.REQUIREMENTS.ADD_MORE_CREDITS}
      >
        <FormattedMessage id="common.addMoreHere" />
      </CustomLink>
    </p>
  );

  const processingJob = jobStatus && jobStatus !== "COMPLETED" && data?.status !== "COMPLETED";

  const processingMessage = processingJob && (
    <div className="box">
      <ProcessingMessage />
    </div>
  );

  const weightsSection = !processingJob && (
    <Weights
      weightsData={weightsData}
      setWeightsData={setWeightsData}
      showWeights={!haveRequirementsChanged && !editingLoading}
      action={
        editingLoading
          ? <Spinner variant="secondary" />
          : haveRequirementsChanged
            ? <button
                onClick={onRequirementsEdition}
                className="btn primary btn--small"
                disabled={editingLoading || isFetchingTokens}
              >
                <FormattedMessage id="common.updateRequirements" />
              </button>
            : <MaxCandidateSelect
                disabled={Boolean(!availableTokens) || editingLoading}
                onClick={handleSubmit}
              />
      }
    />
  );

  const availableTokensMessage = !processingJob && (
    <p className="requirements-page__tokens-available">
      {availableTokens}{" "}
      <FormattedMessage id="common.tokensAvailable" />
      {" - "}
      <CustomLink
        to={"/buy-more"}
        target="_blank"
        className="add-job-spec__link"
        trackingEvent={GA_EVENTS.JOB_SPEC.GET_MORE_UPSELL}
      >
        <FormattedMessage id="common.getMore" />
      </CustomLink>
    </p>
  );

  return (
    <div className="requirements-page">
      <div className="requirements-page__content">
        {pageTitle}

        <JobInformationSection
          editingLoading={editingLoading}
          jobInfo={data?.information}
          onUpdate={onInfoEdition}
        />
        {updateRequirementsMessage}
        <Requirements
          data={data}
          setData={(data) => {
            setHaveRequirementsChanged(true);
            setData(data);
          }}
          disableActions={editingLoading || processingJob}
        />

        {addMoreCreditsMessage}
        {processingMessage}

        {weightsSection}
        {availableTokensMessage}
      </div>
    </div>
  )
}

export default RequirementsPage
